import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import PaginationNumeric from "../../components/PaginationNumeric.jsx";
import TabbedNavigation from "../../components/TabbedNavigation.jsx";
import InlineLoader from "../../components/InlineLoader.jsx";
import DropdownClassic from "../../components/DropdownClassic.jsx";
import ToggleSwitch from "../../components/ToggleSwitch.jsx";
import { getDSPProviders, getMarkets, getMerchants } from "../../api/admin.js";
import { getMerchants as appGetMerchants } from "../../store/app/action.js";
import StoresTable from "./storesTable.jsx";
import {useDispatch, useSelector} from "react-redux";
import {FaCrown} from "react-icons/fa6";

export const MARKETPLACE_ONBOARDING_STEPS = [
  { value: "all", display: "All steps" },
  { value: "legal", display: "1 — Legal Documents" },
  { value: "stripe", display: "2 — Stripe Connect" },
  { value: "licenses", display: "3 — Licenses" },
  { value: "menu", display: "4 — Menu Upload" },
  { value: "under_review", display: "Under Review" },
];

function Stores() {
  const { selectedLocation, dashboardActionCounts } = useSelector((state) => state.app);
  const prospectCount = dashboardActionCounts?.prospect ?? 0;
  const marketplaceOnboardingCount = dashboardActionCounts?.marketplaceOnboarding ?? 0;
  const prevProspectCountRef = useRef();
  const [onboardingStepFilter, setOnboardingStepFilter] = useState("all");
  const merchantsRequestIdRef = useRef(0);
  const [stores, setStores] = useState([]);
  const [sortedStores, setSortedStores] = useState([]);
  const [dspProviders, setDSPProviders] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [markets, setMarkets] = useState([]);
  const [teams, setTeams] = useState([]);
  const [storeType, setStoreType] = useState('direct'); // 'direct', 'marketplace', or 'direct prospects'
  const [showOffboarded, setShowOffboarded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const totalPages = useMemo(() => {
    return Math.ceil(totalRecords / limit);
  }, [totalRecords, limit]);

  const storeTypeTabs = useMemo(
    () => [
      { id: "direct", label: "Direct" },
      {
        id: "direct prospects",
        label: "Direct Prospects",
        badgeCount: prospectCount,
        badgeClassName: "bg-amber-500",
      },
      { id: "marketplace", label: "Marketplace" },
      {
        id: "marketplace-onboarding",
        label: "Marketplace Onboarding",
        badgeCount: marketplaceOnboardingCount,
        badgeClassName: "bg-amber-500",
      },
    ],
    [prospectCount, marketplaceOnboardingCount]
  );

  const _getMerchants = useCallback(
    async (storeTypeParam = storeType) => {
      const requestId = ++merchantsRequestIdRef.current;
      const isCurrent = () => requestId === merchantsRequestIdRef.current;
      try {
        setLoading(true);
        setError(null);
        dispatch(appGetMerchants);
        const isDirectProspects = storeTypeParam === "direct prospects";
        const isMarketplaceOnboarding = storeTypeParam === "marketplace-onboarding";
        const actualStoreType = isDirectProspects
          ? "direct"
          : isMarketplaceOnboarding
          ? "marketplace"
          : storeTypeParam;
        const opts = {};
        if (isMarketplaceOnboarding) {
          opts.onboarding = true;
          opts.sort = "-last_step_completed";
          if (onboardingStepFilter && onboardingStepFilter !== "all") {
            opts.onboarding_step = onboardingStepFilter;
          }
        } else if (storeTypeParam === "marketplace") {
          opts.onboarding = false;
        }
        const res = await getMerchants(page, limit, actualStoreType, isDirectProspects, opts);
        if (!isCurrent()) {
          return;
        }
        let nextStores = [];
        (res.merchants || []).forEach((merchant) => {
          if (merchant.locations?.length) {
            nextStores.push(
              ...merchant.locations.map((loc) => {
                return {
                  ...loc,
                  account: merchant.name,
                };
              })
            );
          }
        });
        setStores(nextStores);
      } catch (err) {
        if (isCurrent()) {
          setError("Failed to fetch stores");
        }
      } finally {
        if (isCurrent()) {
          setLoading(false);
        }
      }
    },
    [page, limit, storeType, onboardingStepFilter, dispatch]
  );
  useEffect(() => {
    if(!stores || !stores.length){
      setSortedStores([]);
      return;
    }
    if(!selectedLocation){
      setSortedStores(stores)
    }else{
      // put selected location on top
      if(storeType === 'direct' || storeType === 'direct prospects'){
        const selected = selectedLocation.direct ? stores.find((store) => store.store_id === selectedLocation.store_id) : null
        const sameAccountAsSelected = selectedLocation.direct ? stores.filter((store) => store.org === selectedLocation.org && store.store_id !== selectedLocation.store_id): [null];
        const rest = stores.filter((store) => store.org !== selectedLocation.org).sort((a, b) => a.org.localeCompare(b.org));
        setSortedStores([selected, ...sameAccountAsSelected, ...rest].filter(store => store));
      }else{
        const selected = !selectedLocation.direct ? stores.find((store) => store.store_id === selectedLocation.store_id) : null;
        const rest = stores.filter((store) => store.store_id !== selectedLocation.store_id).sort((a, b) => a.name.localeCompare(b.name));
        setSortedStores([selected, ...rest].filter(store => store));
      }
  
    }
  }, [stores, selectedLocation]);

  const visibleStores = useMemo(() => {
    if (storeType !== "direct" || showOffboarded) {
      return sortedStores;
    }
    return sortedStores.filter((store) => !store.kill_switch);
  }, [sortedStores, storeType, showOffboarded]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [dspRes, marketsRes] = await Promise.all([
          getDSPProviders(),
          getMarkets()
        ]);
        setDSPProviders(dspRes.providers);
        setMarkets(marketsRes.markets);
        setTeams(marketsRes.teams);
      } catch (err) {
        setError("Failed to load initial data");
      }
    };
    
    fetchInitialData();
  }, []);
  // Handle page/limit changes and store type changes
  useEffect(() => {
    _getMerchants(storeType);
  }, [page, limit, storeType, onboardingStepFilter, _getMerchants]);

  useEffect(() => {
    if (storeType !== "direct prospects") {
      prevProspectCountRef.current = undefined;
      return;
    }
    if (prevProspectCountRef.current === undefined) {
      prevProspectCountRef.current = prospectCount;
      return;
    }
    if (prevProspectCountRef.current !== prospectCount) {
      prevProspectCountRef.current = prospectCount;
      _getMerchants(storeType);
    }
  }, [prospectCount, storeType, _getMerchants]);

  // Error state
  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-4">{error}</div>
            <button 
              onClick={() => _getMerchants(storeType)}
              className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        {/* Title */}
        <div className="mb-4">
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold flex items-center gap-1">
            Stores <FaCrown />
          </h1>
        </div>

        {/* Store Type Tabs */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <TabbedNavigation
            tabs={storeTypeTabs}
            activeTab={storeType}
            onTabChange={(newStoreType) => {
              setStoreType(newStoreType);
              setPage(1); // Reset to first page when store type changes
              setOnboardingStepFilter("all");
            }}
            variant="default"
            size="md"
          />
          {storeType === "direct" && (
            <div className="text-sm text-slate-600 dark:text-slate-300">
              <ToggleSwitch
                checked={showOffboarded}
                onChange={setShowOffboarded}
                onLabel="Show offboarded"
                offLabel="Show offboarded"
                label="Show offboarded stores"
              />
            </div>
          )}
          {storeType === "marketplace-onboarding" && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                Filter by step:
              </span>
              <DropdownClassic
                options={MARKETPLACE_ONBOARDING_STEPS}
                initValue={onboardingStepFilter}
                onSelect={(value) => {
                  setOnboardingStepFilter(value);
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <StoresTable
        stores={visibleStores}
        emptyMessage={
          storeType === "direct" && !showOffboarded && sortedStores.length > 0
            ? "Offboarded stores are hidden."
            : "No stores to show."
        }
        dspProviders={dspProviders}
        getMerchants={_getMerchants}
        markets={markets}
        teams={teams}
        storeType={storeType}
        loading={loading}
      />
      <div className="mt-8">
        <PaginationNumeric
          onSelect={(p) => setPage(p + 1)}
          currentPage={page - 1}
          totalPages={totalPages}
          isFirstPage={page === 1}
          isLastPage={page === totalPages}
        />
      </div>
    </div>
  );
}

export default Stores;
